import { EntryMethod } from 'components/enter/EntryMethod';
import EnterDrawCountdown from 'components/enter2026/EnterDrawCountdown';
import EnterLayout from 'components/enter2026/EnterLayout';
import ExpandableOrderSummary from 'components/enter2026/ExpandableOrderSummary';
import ImageWithFallback from 'components/ImageWithFallback';
import MainLayout from 'components/layout/MainLayout';
import RadioToggleButton from 'components/RadioToggleButton';
import RadioToggleButtonFlexContainer from 'components/RadioToggleButtonFlexContainer';
import RoundedButton from 'components/RoundedButton';
import TrustPilotTwoLinesWidget from 'components/TrustPilotTwoLinesWidget';
import {
  DaymadeDrawTypePlanCode,
  MainDrawDay,
  MainDrawTypeCode,
} from 'constants/Draw';
import { useEnterFlowPreconditionCheck } from 'hooks/enter/useEnterFlowPreconditionCheck';
import { useDrawStore } from 'hooks/stores/useDrawStore';
import { useEnterStore } from 'hooks/stores/useEnterStore';
import { observer } from 'mobx-react-lite';
import { GetServerSideProps } from 'next';
import { useRouter } from 'next/router';
import { useEffect } from 'react';
import { getChooseYourPlanBoostValue } from 'utils/ab-test/abTests';
import { tryApi } from 'utils/api/common';
import { drawApiClient } from 'utils/apiClient';
import HttpStatusCode from 'utils/HttpStatusCode';
import TealCircleCalendar from '../../components/svg/TealCircleCalendar.svg';
import styles from './3.module.css';

interface EnterNowStep3Props {
  nextDrawDateIsoString: string | null | undefined;
}

const EnterNowStep3 = observer((props: EnterNowStep3Props) => {
  const router = useRouter();
  const enterStore = useEnterStore();
  const drawStore = useDrawStore();

  useEffect(() => {
    // Fix weirdness when transitioning from free enter flow to normal enter flow
    if (enterStore.plan?.code === DaymadeDrawTypePlanCode.FREE) {
      enterStore.setEntryMethod(EntryMethod.PremiumSubscription);
    }
  }, [enterStore.plan?.code]);
  useEnterFlowPreconditionCheck();

  const entryCounts = Object.entries(enterStore.plan?.priceTable[0].GBP || []);
  const daysValue = enterStore.days.length === 2 ? 'BOTH' : enterStore.days[0];

  return (
    <MainLayout limitItemWidths>
      <EnterLayout
        sidePanel={
          <>
            <EnterDrawCountdown
              date={
                props.nextDrawDateIsoString ??
                drawStore.upcomingDrawDate?.toISOString()
              }
            />

            <div className={styles.treesImage}>
              <ImageWithFallback
                src="https://daymade-website-assets.s3.eu-west-1.amazonaws.com/2026/enter/PlantTrees.png"
                alt="Veritree planting trees, DAYMADE has planted over 4,500,000"
                layout="responsive"
                width="936"
                height="1072"
              />
            </div>
          </>
        }
        sidePanelBackground={'TEAL'}
        stepIcon={<TealCircleCalendar />}
        stepName="Draw Days"
        stepIntroText={
          'Winning numbers are drawn every week.\nWhich days do you want to play?'
        }
        stepIndex={2}
        stepTooltipContent={
          <div>
            <p>
              Winning numbers are drawn every Friday & Tuesday. Choose whether
              you want to be entered into the Tuesday Draw, the Friday Draw, or
              both.
            </p>
            <p>
              The two draws are identical and you’ll be entered with the same
              numbers and amount of entries.
            </p>
            <p>
              For every draw day you enter, 1 tree will be planted with our
              partner, veritree.
            </p>
          </div>
        }
      >
        <div className={styles.dayPicker}>
          <RadioToggleButtonFlexContainer>
            <RadioToggleButton
              radioGroupName="entryDay"
              value={'TUESDAY'}
              selectedValue={daysValue}
              onChange={() => enterStore.setDays([MainDrawDay.TUESDAY])}
              text="Tuesday"
            />
            <RadioToggleButton
              radioGroupName="entryDay"
              value={'FRIDAY'}
              selectedValue={daysValue}
              onChange={() => enterStore.setDays([MainDrawDay.FRIDAY])}
              text="Friday"
            />
            <RadioToggleButton
              radioGroupName="entryDay"
              value={'BOTH'}
              selectedValue={daysValue}
              onChange={() =>
                enterStore.setDays([MainDrawDay.TUESDAY, MainDrawDay.FRIDAY])
              }
              badgeText={'🎉 DOUBLE YOUR CHANCES 🎉'}
              badgeStyle="STYLE1"
              text="Tuesday & Friday"
            />
          </RadioToggleButtonFlexContainer>
        </div>
        <div className={'aboutUsTypographyBodySmall ' + styles.drawFacts}>
          <p>
            <strong>🌳 1 draw = 1 tree</strong>
          </p>
          <p>
            <strong>
              🌲 {drawStore.treesPlantedCount?.toLocaleString()} trees planted
            </strong>
          </p>
        </div>
        <ExpandableOrderSummary showDays={true} />
        <RoundedButton
          rebrandDm2025
          fullWidth
          href={
            // ORIGINALV2 is boosted already so skip boost step
            getChooseYourPlanBoostValue() !== 'ORIGINALV2'
              ? '/enternow/4'
              : '/enternow/5'
          }
        >
          Continue
        </RoundedButton>
        <TrustPilotTwoLinesWidget />
      </EnterLayout>
    </MainLayout>
  );
});

export default EnterNowStep3;

export const getServerSideProps: GetServerSideProps<
  EnterNowStep3Props
> = async (_ctx) => {
  const [resp] = await Promise.all([
    tryApi(
      drawApiClient.public.getDrawTypeCached({
        params: { draw_code: MainDrawTypeCode },
      }),
    ),
  ]);

  let nextDrawDate: string | null = null;

  if (resp.status === HttpStatusCode.OK) {
    if (resp.body.next_draw?.drawDate) {
      nextDrawDate = new Date(resp.body.next_draw.drawDate).toISOString();
    }
  }

  return {
    props: {
      nextDrawDateIsoString: nextDrawDate,
    },
  };
};
